import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { fmtCLP, fmtFecha, formatPagos } from "../utils/formato";
import toast from "../utils/toast";
import ConfirmModal from "../components/ConfirmModal";
import { imprimirComanda } from "../utils/imprimirComanda";
import "./PedidosManager.css";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx-js-style";
import React from "react";

/* =======================
   Helpers
======================= */
const N = (v) => Number(v || 0);
const pagoTexto = (p) => formatPagos(p?.pagos, p?.medio_pago || "");
const pagoComandaTexto = (p) => Array.isArray(p?.pagos) && p.pagos.length > 1 ? pagoTexto(p) : (p?.medio_pago || "");
const numeroComandaPedido = (p) => p?.numero_comanda ?? p?.numeroComanda ?? p?.id ?? "—";

const boolValue = (value) => value === true || String(value).toLowerCase() === "true";
const esCuentaPersonal = (p) => boolValue(p?.cuenta_personal);
const esTrabajador = (p) => boolValue(p?.trabajador);
const esTrabajadorConvertido = (p) => !esTrabajador(p) && (boolValue(p?.trabajador_convertido) || Boolean(p?.venta_en));
const coincideTipo = (p, tipo) => tipo === "trabajadores" ? esTrabajador(p) : !esTrabajador(p);
const pagoItemPagado = (pago) => pago?.pagado === true || String(pago?.pagado).toLowerCase() === "true";
const pedidoPagado = (p) => {
  if (Array.isArray(p?.pagos) && p.pagos.length) {
    return p.pagos.every(pagoItemPagado);
  }
  return boolValue(p?.pagado);
};
const fmtDiaResumen = (value) => {
  const datePart = String(value || "").slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    const [year, month, day] = datePart.split("-");
    return `${day}-${month}-${year}`;
  }
  return value || "";
};
const subtotalItems = (p) => (p?.items || []).reduce(
  (acc, i) => acc + N(i.precio) * N(i.cantidad),
  0
);

/* =======================
   Helpers CSV (opcional)
======================= */
function downloadCSV(filename, rows) {
  if (!rows || !rows.length) return;
  const headers = Object.keys(rows[0]);
  const csv =
    headers.join(",") +
    "\n" +
    rows
      .map((r) =>
        headers
          .map((h) => {
            const v = r[h] ?? "";
            const s = typeof v === "string" ? v : JSON.stringify(v);
            return `"${String(s).replaceAll('"', '""')}"`;
          })
          .join(",")
      )
      .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/* =======================
   Excel utils
======================= */
// ajusta ancho de columnas en base al contenido
function autoColsFrom(rows) {
  const widths = [];
  rows.forEach((r) => {
    r.forEach((v, i) => {
      const len = (v ? String(v) : "").length;
      widths[i] = Math.max(widths[i] || 10, Math.min(len + 4, 60));
    });
  });
  return widths.map((w) => ({ wch: w }));
}

function buildResumenSheet(pedidos) {
  const pedidosVenta = pedidos.filter((p) => !esCuentaPersonal(p) && !esTrabajador(p));
  // Usamos total neto para el resumen
  const totalNeto = pedidosVenta.reduce((s, p) => s + N(p._totalNeto), 0);
  const porMedio = pedidosVenta.reduce((acc, p) => {
    const k = p.medio_pago || "—";
    acc[k] = (acc[k] || 0) + N(p._totalNeto);
    return acc;
  }, {});

  const rows = [
    ["Resumen"],
    ["Fecha exportación", new Date().toLocaleString("es-CL")],
    ["Pedidos venta", pedidosVenta.length],
    ["Total neto", fmtCLP(totalNeto)],
    [],
    ["Totales netos por medio de pago", ""],
    ["Medio", "Total neto"],
    ...Object.entries(porMedio).map(([m, t]) => [m, fmtCLP(t)]),
  ];

  const ws = XLSX.utils.aoa_to_sheet(rows);

  // Estilos básicos
  ws["A1"] = { v: "Resumen", t: "s", s: { font: { bold: true, sz: 14 } } };
  const boldKeys = ["A3", "A4", "A6", "A7"];
  boldKeys.forEach((addr) => {
    if (ws[addr]) ws[addr].s = { font: { bold: true } };
  });

  ws["!cols"] = autoColsFrom(rows);
  return ws;
}

function buildPedidosSheet(pedidos) {
  const header = [
    "N° Comanda",
    "Fecha",
    "Tipo",
    "Cliente",
    "Hora retiro",
    "Medio pago",
    "Pagado",
    "Subtotal",
    "Descuento",
    "Total neto",
    "Recibido por",
    "Items",
    "Observación",
  ];

  const data = pedidos.map((p) => {
    const items = (p.items || [])
      .map((i) => `${i.nombre} x${N(i.cantidad)} (${fmtCLP(i.precio)})`)
      .join("\n"); // cada item en línea separada

    return [
      numeroComandaPedido(p),
      fmtFecha(p.venta_en || p.creado_en),
      esTrabajador(p) ? "Trabajador" : esTrabajadorConvertido(p) ? "Trabajador pagado" : "Venta",
      p.cliente || "",
      p.hora_retiro || "",
      esTrabajador(p) ? "" : (p.medio_pago || ""),
      esTrabajador(p) ? "" : (pedidoPagado(p) ? "Sí" : "No"),
      fmtCLP(p._subtotal),
      fmtCLP(p._descMonto),
      fmtCLP(p._totalNeto),
      p.usuario_nombre || "",
      items,
      p.observacion || "",
    ];
  });

  const rows = [header, ...data];
  const ws = XLSX.utils.aoa_to_sheet(rows);

  // Estilo encabezado
  const headStyle = {
    font: { bold: true, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: "E53935" } },
    alignment: { horizontal: "center", vertical: "center" },
  };
  header.forEach((_, idx) => {
    const addr = XLSX.utils.encode_cell({ r: 0, c: idx });
    if (ws[addr]) ws[addr].s = headStyle;
  });

  // Ajustes de columnas
  ws["!cols"] = autoColsFrom(rows);

  // wrapText en “Items” y “Observación”
  const itemsCol = header.indexOf("Items");
  const obsCol = header.indexOf("Observación");
  for (let r = 1; r < rows.length; r++) {
    [itemsCol, obsCol].forEach((c) => {
      const addr = XLSX.utils.encode_cell({ r, c });
      if (ws[addr]) {
        ws[addr].s = { alignment: { wrapText: true, vertical: "top" } };
      }
    });
  }

  return ws;
}

/* =======================================
   Sub-componente: Resumen por día con paginador
======================================= */
const PAGE_SIZES = [3, 5, 10, 20];

function ResumenDias({ resumen }) {
  const [pageDias, setPageDias] = useState(1);
  const [sizeDias, setSizeDias] = useState(3);

  const totalDias = resumen.length;
  const totalPages = Math.ceil(totalDias / sizeDias);
  const slice = resumen.slice((pageDias - 1) * sizeDias, pageDias * sizeDias);

  const handleSize = (e) => {
    setSizeDias(Number(e.target.value));
    setPageDias(1);
  };

  return (
    <div className="pm-table-wrap" style={{ marginBottom: 12 }}>
      <div className="pm-preview-head" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span>Resumen por día</span>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: ".8rem", color: "#888", fontWeight: 600 }}>
          Filas:
          <select className="pm-select" style={{ padding: "4px 8px", fontSize: ".8rem" }} value={sizeDias} onChange={handleSize}>
            {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </div>
      <div className="pm-preview-table">
        <table className="pm-table pm-resumen-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th className="right">Pedidos</th>
              <th className="right">Efectivo</th>
              <th className="right">Transferencia</th>
              <th className="right">Tarjeta</th>
              <th className="right">Total</th>
            </tr>
          </thead>
          <tbody>
            {slice.map((r) => (
              <tr key={r.dia}>
                <td className="nowrap">{fmtDiaResumen(r.dia)}</td>
                <td className="right">{r.pedidos}</td>
                <td className="right">{N(r.efectivo) > 0 ? fmtCLP(r.efectivo) : <span className="muted">—</span>}</td>
                <td className="right">{N(r.transferencia) > 0 ? fmtCLP(r.transferencia) : <span className="muted">—</span>}</td>
                <td className="right">{N(r.tarjeta) > 0 ? fmtCLP(r.tarjeta) : <span className="muted">—</span>}</td>
                <td className="right"><strong>{fmtCLP(r.total)}</strong></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 12px" }}>
          <button className="btn btn-sm" disabled={pageDias <= 1} onClick={() => setPageDias((p) => p - 1)}>Anterior</button>
          <span style={{ fontSize: ".85rem", color: "#888" }}>Página {pageDias} de {totalPages}</span>
          <button className="btn btn-sm" disabled={pageDias >= totalPages} onClick={() => setPageDias((p) => p + 1)}>Siguiente</button>
        </div>
      )}
    </div>
  );
}

/* =======================================
   Componente principal PedidosManager
======================================= */
export default function PedidosManager({ usuario }) {
  const navigate = useNavigate();
  const puedeEliminarPedidos = usuario?.rol === "superadmin";

  // filtros
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [q, setQ] = useState("");
  const [medio, setMedio] = useState("");
  const [pagado, setPagado] = useState(""); // "", "true", "false"
  const [tipo, setTipo] = useState("ventas"); // "ventas" | "trabajadores"

  // datos
  const [pedidos, setPedidos] = useState([]);
  const [resumen, setResumen] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [cargando, setCargando] = useState(true);
  const [err, setErr] = useState("");

  // confirmación eliminar
  const [pedidoAEliminar, setPedidoAEliminar] = useState(null);

  // edición medio de pago
  const [editandoMedio, setEditandoMedio] = useState(null); // id del pedido en edición
  const [medioDraft, setMedioDraft] = useState("");

  // filas expandibles
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const params = useMemo(
    () => ({
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
      ...(q ? { q } : {}),
      ...(tipo === "ventas" && medio ? { medio_pago: medio } : {}),
      ...(tipo === "ventas" && pagado ? { pagado } : {}),
      trabajador: tipo === "trabajadores" ? "true" : "false",
      limit: pageSize,
      offset: (page - 1) * pageSize,
    }),
    [from, to, q, medio, pagado, page, pageSize, tipo]
  );

  const fetchPedidos = async () => {
    setCargando(true);
    setErr("");
    try {
      const { data } = await api.get("/api/pedidos", { params });

      // Enriquecemos con descuento y total neto
      // En la DB: p.total = total final (ya con descuento aplicado), p.descuento = monto descontado
      const enriched = (data?.rows || []).map((p) => {
        const descMonto = N(p.descuento);
        const totalNeto = N(p.total_neto ?? p.total);
        const subtotal  = N(p.subtotal) || totalNeto + descMonto || subtotalItems(p);

        return {
          ...p,
          _subtotal: subtotal,
          _descMonto: descMonto,
          _totalNeto: totalNeto,
        };
      });
      const filtradosPorTipo = enriched.filter((p) => coincideTipo(p, tipo));

      setPedidos(filtradosPorTipo);
      setTotal(filtradosPorTipo.length === enriched.length ? (data.total ?? enriched.length) : filtradosPorTipo.length);

      if (tipo === "ventas") {
        const r = await api.get("/api/pedidos/resumen", { params: { from, to, q, medio_pago: medio, pagado } });
        setResumen(
          (r.data?.rows || []).sort(
            (a, b) => new Date(b.dia) - new Date(a.dia)
          )
        );
      } else {
        setResumen([]);
      }
    } catch (e) {
      setErr(e?.response?.data?.error || e.message);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    fetchPedidos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, q, medio, pagado, page, pageSize, tipo]);

  useEffect(() => {
    setPage(1);
  }, [from, to, q, medio, pagado, pageSize, tipo]);

  useEffect(() => {
    if (tipo === "trabajadores") {
      setEditandoMedio(null);
    }
  }, [tipo]);

  // Total mostrado arriba: usamos total neto
  const totalFiltradoNeto = useMemo(
    () => pedidos.reduce((acc, p) => esCuentaPersonal(p) || esTrabajador(p) ? acc : acc + N(p._totalNeto), 0),
    [pedidos]
  );

  /* =====================
     Export CSV (opcional)
  ====================== */
  const exportarCSV = () => {
    const flat = pedidos.map((p) => ({
      numero_comanda: numeroComandaPedido(p),
      fecha: fmtFecha(p.venta_en || p.creado_en),
      tipo: esTrabajador(p) ? "Trabajador" : esTrabajadorConvertido(p) ? "Trabajador pagado" : "Venta",
      cliente: p.cliente || "",
      hora_retiro: p.hora_retiro || "",
      medio_pago: esTrabajador(p) ? "" : (p.medio_pago || ""),
      pagado: esTrabajador(p) ? "" : (pedidoPagado(p) ? "Sí" : "No"),
      subtotal: N(p._subtotal),
      descuento: N(p._descMonto),
      total_neto: N(p._totalNeto),
      recibido_por: p.usuario_nombre || "",
      items: (p.items || [])
        .map((i) => `${i.nombre} x${N(i.cantidad)} (${i.precio})`)
        .join(" | "),
      observacion: p.observacion || "",
    }));
    downloadCSV("pedidos.csv", flat);
  };

  /* ==========================
     Export Excel con estilo
  =========================== */
  const exportarExcel = () => {
    if (!pedidos?.length) {
      toast("No hay pedidos para exportar");
      return;
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, buildResumenSheet(pedidos), "Resumen");
    XLSX.utils.book_append_sheet(wb, buildPedidosSheet(pedidos), "Pedidos");

    const rangoTexto =
      from && to ? `${from}_a_${to}` : from || to || new Date().toISOString().slice(0, 10);
    const nombre = `pedidos_${rangoTexto}.xlsx`;
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      nombre
    );
  };

  const confirmarEliminar = async () => {
    const id = pedidoAEliminar;
    setPedidoAEliminar(null);
    try {
      await api.delete(`/api/pedidos/${id}`);
      setExpanded((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      fetchPedidos();
    } catch (e) {
      toast(e?.response?.data?.error || "No se pudo eliminar");
    }
  };

  const guardarMedioPago = async (id) => {
    try {
      await api.patch(`/api/pedidos/${id}/medio-pago`, { medio_pago: medioDraft });
      setPedidos((prev) => prev.map((p) => p.id === id ? { ...p, medio_pago: medioDraft } : p));
      setEditandoMedio(null);
    } catch (e) {
      toast(e?.response?.data?.error || "No se pudo actualizar");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pedidosInfo = total > 0
    ? tipo === "trabajadores"
      ? `${total} pedidos de trabajadores${total > pageSize ? ` (mostrando ${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, total)})` : ""}`
      : `${total} pedidos${total > pageSize ? ` (mostrando ${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, total)})` : ""} | Total neto (pagina): ${fmtCLP(totalFiltradoNeto)}`
    : "0 pedidos";

  const pedidosTableToolbar = (
    <div className="pm-table-toolbar">
      <div className="pm-count">{pedidosInfo}</div>

      <div className="pm-pagination" aria-label="Paginador de pedidos">
        <button
          type="button"
          className="btn btn-sm"
          disabled={page <= 1}
          onClick={() => setPage((p) => p - 1)}
        >
          Anterior
        </button>
        <span className="muted">
          Pagina {page} de {totalPages}
        </span>
        <button
          type="button"
          className="btn btn-sm"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
        >
          Siguiente
        </button>
        <label className="pm-page-size">
          Items por pagina:
          <select
            className="pm-select pm-select--compact"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {[10, 25, 50, 100].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </div>
    </div>
  );



  return (
    <div className="pm-page">
      <div className="pm-header">
        <h1>Mantenedor de Pedidos</h1>

        <div className="pm-tools">
          <input
            className="pm-search"
            placeholder="Buscar cliente o producto…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />

          <label className="pm-date-label">
            Desde
            <input
              className="pm-select"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </label>
          <label className="pm-date-label">
            Hasta
            <input
              className="pm-select"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </label>
          {(from || to) && (
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => { setFrom(""); setTo(""); }}
              title="Limpiar fechas"
            >
              ✕ Fechas
            </button>
          )}

          <select
            className="pm-select"
            value={medio}
            disabled={tipo === "trabajadores"}
            onChange={(e) => setMedio(e.target.value)}
          >
            <option value="">Medio de pago (todos)</option>
            <option value="Efectivo">Efectivo</option>
            <option value="Transferencia">Transferencia</option>
            <option value="Tarjeta">Tarjeta</option>
          </select>

          <select
            className="pm-select"
            value={pagado}
            disabled={tipo === "trabajadores"}
            onChange={(e) => setPagado(e.target.value)}
          >
            <option value="">Pagado (todos)</option>
            <option value="true">Sí</option>
            <option value="false">No</option>
          </select>

          <button className="btn" onClick={fetchPedidos}>
            Actualizar
          </button>
          <button className="btn" onClick={exportarCSV}>
            Exportar CSV
          </button>
          <button className="btn btn-accent" onClick={exportarExcel}>
            Exportar Excel
          </button>
        </div>
      </div>

      <div className="pm-tabs" role="tablist" aria-label="Tipo de pedidos">
        <button
          type="button"
          className={`pm-tab${tipo === "ventas" ? " is-active" : ""}`}
          onClick={() => setTipo("ventas")}
          role="tab"
          aria-selected={tipo === "ventas"}
        >
          Ventas
        </button>
        <button
          type="button"
          className={`pm-tab${tipo === "trabajadores" ? " is-active" : ""}`}
          onClick={() => setTipo("trabajadores")}
          role="tab"
          aria-selected={tipo === "trabajadores"}
        >
          Trabajadores
        </button>
      </div>

      {err && <div className="pm-alert error">Error: {err}</div>}

      <div className="pm-count pm-count--legacy">
        {cargando
          ? "Cargando…"
          : total > 0
            ? tipo === "trabajadores"
              ? `${total} pedidos de trabajadores${total > pageSize ? ` (mostrando ${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, total)})` : ""}`
              : `${total} pedidos${total > pageSize ? ` (mostrando ${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, total)})` : ""} | Total neto (página): ${fmtCLP(totalFiltradoNeto)}`
            : "0 pedidos"}
      </div>

      {!cargando && (
        <div className="pm-pagination pm-pagination--legacy" style={{ marginBottom: 8, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button
            type="button"
            className="btn btn-sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Anterior
          </button>
          <span className="muted">
            Página {page} de {Math.max(1, Math.ceil(total / pageSize))}
          </span>
          <button
            type="button"
            className="btn btn-sm"
            disabled={page >= Math.ceil(total / pageSize)}
            onClick={() => setPage((p) => p + 1)}
          >
            Siguiente
          </button>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: ".8rem", color: "#888", fontWeight: 600, marginLeft: "auto" }}>
            Filas por página:
            <select
              className="pm-select"
              style={{ padding: "4px 8px", fontSize: ".8rem" }}
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {[10, 25, 50, 100].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        </div>
      )}

      {/* Resumen por día */}
      {!!resumen.length && <ResumenDias resumen={resumen} />}

      {/* Tabla de pedidos */}
      {cargando ? (
        <div className="pm-empty">Cargando pedidos…</div>
      ) : pedidos.length ? (
        <div className="pm-table-wrap">
          {pedidosTableToolbar}
          <table className="pm-table">
            <thead>
              <tr>
                <th>Comanda</th>
                <th>Fecha</th>
                <th>Cliente</th>
                <th>Medio</th>
                <th className="center">Pagado</th>
                <th className="right">Total neto</th>
                <th className="center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {pedidos.map((p) => {
                const isOpen = expanded.has(p.id);
                const itemsArr = p.items || [];
                const worker = esTrabajador(p);
                const workerPaid = esTrabajadorConvertido(p);

                return (
                  <React.Fragment key={p.id}>
                    {/* fila principal */}
                    <tr className={isOpen ? "row-open" : ""}>
                      <td>{numeroComandaPedido(p)}</td>
                      <td>
                        {fmtFecha(p.venta_en || p.creado_en)}
                        {p.hora_retiro ? (
                          <div className="sub">Retiro: {p.hora_retiro}</div>
                        ) : null}
                      </td>
                      <td>{p.cliente || "-"}</td>
                      <td>
                        {worker ? (
                          <span className="pm-worker-tag">Trabajador</span>
                        ) : editandoMedio === p.id ? (
                          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                            <select
                              className="pm-select"
                              style={{ padding: "4px 8px", fontSize: ".85rem" }}
                              value={medioDraft}
                              onChange={(e) => setMedioDraft(e.target.value)}
                              autoFocus
                            >
                              <option value="Efectivo">Efectivo</option>
                              <option value="Transferencia">Transferencia</option>
                              <option value="Tarjeta">Tarjeta</option>
                            </select>
                            <button className="btn btn-sm" onClick={() => guardarMedioPago(p.id)} title="Guardar">✓</button>
                            <button className="btn btn-sm" onClick={() => setEditandoMedio(null)} title="Cancelar">✕</button>
                          </div>
                        ) : (
                          <div className="pm-payment-cell">
                            {workerPaid && <span className="pm-worker-paid-tag">Trabajador pagado</span>}
                            <span
                              style={{ cursor: "pointer", borderBottom: "1px dashed #bbb" }}
                              title="Clic para editar"
                              onClick={() => { setEditandoMedio(p.id); setMedioDraft(p.medio_pago || "Efectivo"); }}
                            >
                              {pagoTexto(p) || "-"}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="center">{worker ? "-" : (pedidoPagado(p) ? "Sí" : "No")}</td>
                      <td className="right">{fmtCLP(p._totalNeto)}</td>

                      <td className="center">
                        <button
                          className="btn btn-sm"
                          onClick={() => toggleExpand(p.id)}
                          aria-expanded={isOpen}
                          aria-controls={`pedido-expand-${p.id}`}
                          title={isOpen ? "Contraer" : "Expandir"}
                          style={{ marginRight: 6 }}
                        >
                          {isOpen ? "−" : "+"}
                        </button>
                        <button
                          className="btn btn-sm"
                          title="Reimprimir comanda"
                          onClick={() => imprimirComanda({
                            numeroComanda: numeroComandaPedido(p),
                            cliente: p.cliente,
                            telefono: p.telefono,
                            canal: p.canal,
                            horaRetiro: p.hora_retiro,
                            medioPago: worker ? "TRABAJADOR" : pagoComandaTexto(p),
                            pagado: worker ? false : pedidoPagado(p),
                            observacion: p.observacion || '',
                            pedido: (p.items || []).map((i) => ({
                              nombre: i.nombre,
                              cantidad: i.cantidad,
                              precio: i.precio,
                              cambios: i.cambios || (Array.isArray(i.modificaciones) ? i.modificaciones.join("\n") : ""),
                            })),
                            subtotal: p._subtotal,
                            descuento: p._descMonto,
                            totalFinal: p._totalNeto,
                            trabajador: worker,
                          })}
                        >
                          🖨
                        </button>
                        <button
                          className="btn btn-sm btn-accent"
                          onClick={() => navigate(`/pos?pedido=${p.id}`)}
                        >
                          Continuar pedido
                        </button>
                      
                        <button
                          className="btn btn-sm danger"
                          disabled={!puedeEliminarPedidos}
                          onClick={() => setPedidoAEliminar(p.id)}
                          title={
                            puedeEliminarPedidos
                              ? "Eliminar pedido"
                              : "Solo superadmin puede eliminar pedidos"
                          }
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>

                    {/* fila expandida */}
                    {isOpen && (
                      <tr
                        id={`pedido-expand-${p.id}`}
                        className="row-expand"
                      >
                        <td colSpan={7}>
                          <div className="expand-grid">
                            <section>
                              <h4>Ítems</h4>
                              <ul className="items-list">
                                {itemsArr.map((i, idx) => (
                                  <li key={idx}>
                                    <span className="name">{i.nombre}</span>
                                    <span className="qty">x{i.cantidad}</span>
                                    <span className="price">
                                      {fmtCLP(i.precio)}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </section>

                            <section>
                              <h4>Observación</h4>
                              <p className="obs">
                                {p.observacion || "—"}
                              </p>
                            </section>

                            <section className="meta">
                              <div>
                                <b>Canal:</b> {p.canal || "—"}
                              </div>
                              <div>
                                <b>Retiro:</b> {p.hora_retiro || "—"}
                              </div>
                              <div>
                                <b>Comanda:</b> {numeroComandaPedido(p)}
                              </div>
                              <div>
                                <b>Recibido por:</b>{" "}
                                {p.usuario_nombre || "—"}
                              </div>
                              {worker && (
                                <div>
                                  <b>Tipo:</b> Trabajador
                                </div>
                              )}
                              <div>
                                <b>Pagos:</b>
                                <div className="pm-payments">
                                  {worker ? (
                                    <span className="pm-payment">No aplica</span>
                                  ) : (() => {
                                    const pagosList = Array.isArray(p.pagos) && p.pagos.length
                                      ? p.pagos
                                      : [{ medio: p.medio_pago, monto: p._totalNeto, pagado: pedidoPagado(p) }];

                                    return pagosList.map((pago, idx) => {
                                      // Si sólo hay un medio, no mostrar "Pendiente" aquí; la columna "Pagado" indica el estado.
                                      if (pagosList.length === 1) {
                                        return (
                                          <span className="pm-payment" key={`${pago.medio}-${idx}`}>
                                            {pago.medio || "-"} {fmtCLP(pago.monto)}
                                          </span>
                                        );
                                      }

                                      // Si hay 2 medios, mostrar cuál porción está pagada/pendiente.
                                      return (
                                        <span className={`pm-payment ${pagoItemPagado(pago) ? "paid" : "pending"}`} key={`${pago.medio}-${idx}`}>
                                          {pago.medio || "-"} {fmtCLP(pago.monto)} · {pagoItemPagado(pago) ? "Pagado" : "Pendiente"}
                                        </span>
                                      );
                                    });
                                  })()}
                                </div>
                              </div>
                              <div>
                                <b>Descuento:</b>{" "}
                                {N(p._descMonto) > 0 ? fmtCLP(p._descMonto) : "—"}
                              </div>
                              <div>
                                <b>Total neto:</b>{" "}
                                {fmtCLP(p._totalNeto)}
                              </div>
                             
                            </section>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="pm-empty">
          {tipo === "trabajadores"
            ? "No hay pedidos de trabajadores con los filtros actuales."
            : "No hay pedidos con los filtros actuales."}
        </div>
      )}

      {pedidoAEliminar && (
        <ConfirmModal
          mensaje={`¿Eliminar comanda #${numeroComandaPedido(pedidos.find((p) => p.id === pedidoAEliminar))}? Esta acción no se puede deshacer.`}
          textoConfirmar="Eliminar"
          onConfirmar={confirmarEliminar}
          onCancelar={() => setPedidoAEliminar(null)}
        />
      )}
    </div>
  );
}
